import { Component, NgZone, OnInit, inject, viewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { Campaign, CampaignAdminRequest, CampaignLocationFilter, CampaignsCampaignAdminApiClient, CampaignsCampaignLocationFilterAdminApiClient, CoinReward, Counter, Delay, Filter, Parallel, Reward, Sequential } from 'src/app/api/api-campaigns.service.generated';
import { MessageDialogModel } from 'src/app/common/message-dialog/message-dialog.component';
import { CampaignBaseEditComponent } from '../_/campaign-base-edit/campaign-base-edit.component';
import { CampaignLocationFiltersComponent } from '../_/campaign-location-filters/campaign-location-filters.component';
import { ActionContext, ActionOperation, StepActionService } from './step-action.service';
import { EmptyStrategy, StrategyName } from './strategies/strategy/strategy.component';
import { StrategyEditPanelComponent } from './strategy-edit-panel/strategy-edit-panel.component';
import { PERMISSION } from 'src/app/const/permission';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { FlexLayoutModule } from '@angular/flex-layout';
import { StrategyComponent } from './strategies/strategy/strategy.component';
import { CampaignMediaComponent } from '../_/campaign-media/campaign-media.component';
import { PageContainerComponent, BreadcrumbsComponent, ButtonComponent } from 'wecheer-common';
import { HasAnyPermissionDirective } from 'src/app/common/directives/has-any-permission/has-any-permission.directive';
import { ScreenService } from 'src/app/common/services/screen.service';
import { AppVersionService } from 'src/app/common/services/app-version.service';
import { MatTabsModule } from '@angular/material/tabs';
import { CampaignCloneService } from 'src/app/common/services/campaign-clone.service';

@Component({
  selector: 'app-multi-campaign-edit',
  templateUrl: './multi-campaign-edit.component.html',
  styleUrls: ['./multi-campaign-edit.component.scss'],
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatCardModule,
    MatTabsModule,
    FlexLayoutModule,
    CampaignBaseEditComponent,
    CampaignLocationFiltersComponent,
    StrategyComponent,
    CampaignMediaComponent,
    StrategyEditPanelComponent,
    HasAnyPermissionDirective,
    PageContainerComponent,
    BreadcrumbsComponent,
    ButtonComponent
]
})
export class MultiCampaignEditComponent implements OnInit {
  private stepAction = inject(StepActionService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private campaignsApi = inject(CampaignsCampaignAdminApiClient);
  private campaignLocationFiltersApi = inject(CampaignsCampaignLocationFilterAdminApiClient);
  private campaignCloneService = inject(CampaignCloneService);
  private zone = inject(NgZone);
  screen = inject(ScreenService);
  appVersion = inject(AppVersionService);


  readonly campaignBase = viewChild<CampaignBaseEditComponent>('campaignBase');
  readonly editPanel = viewChild<StrategyEditPanelComponent>('editPanel');
  readonly campaignLocationFilters = viewChild<CampaignLocationFiltersComponent>('campaignLocationFilters');

  editModel: CampaignAdminRequest;

  campaign: Campaign;

  campaignReplay = new BehaviorSubject<Campaign>(undefined);

  campaignId: string;
  advertiserId: string;
  originalCampaignId: string;
  clonedLocationFilters: CampaignLocationFilter;

  isLoading: boolean;
  isAdding: boolean;
  isCloning: boolean;

  viewMode: 'scheme' | 'locations' | 'json' | 'media' | '' = 'scheme';
  readonly permissions = PERMISSION;

  constructor() {

    this.isAdding = this.route.snapshot.params.campaignId == 'new';
    this.isCloning = this.route.snapshot.queryParams.clone === 'true';
    this.campaignId = this.isAdding ? undefined : this.route.snapshot.params.campaignId;
    this.advertiserId = this.route.snapshot.params.advertiserId;
    this.originalCampaignId = this.campaignId;
  }

  async ngOnInit() {
    try {
      this.isLoading = true;
      if (this.isCloning) {
        await this.cloneCampaign();
      } else {
        this.campaign = this.campaignId
          ? await lastValueFrom(this.campaignsApi.getCampaign(this.campaignId))
          : new Campaign({ advertiserId: this.advertiserId });
      }
    } catch (err: any) {
    } finally {
      this.isLoading = false;
    }

    // Load location filters - following punch-card pattern
    if (this.isCloning) {
      // When cloning, location filters are already loaded in cloneCampaign()
      this.campaignLocationFilters().setFormValue(this.clonedLocationFilters);
    } else {
      const clf = this.campaignId
        ? await lastValueFrom(this.campaignLocationFiltersApi.getByCampaignId(this.campaignId))
        : new CampaignLocationFilter({ campaignId: this.campaignId, filters: [] });
      this.campaignLocationFilters().setFormValue(clf);
    }

    this.campaignReplay.next(this.campaign);


    this.stepAction.addNextStep.subscribe(ctx => {
      this.zone.run(() => {
        this.addNextStep(ctx);
      });
    });
    this.stepAction.editStep.subscribe(ctx => {
      this.zone.run(() => {
        this.editStep(ctx);
      });
    });
    this.stepAction.deleteStep.subscribe(ctx => {
      // this.zone.run(() => {
      this.deleteStep(ctx);
      //});
    });
  }

  onAddFirstStep() {
    const ctx = { operation: ActionOperation.Add } as ActionContext;
    this.editPanel().open(ctx);
  }

  addNextStep(ctx: ActionContext) {
    ctx.edited = EmptyStrategy.new();  // next step;
    this.editPanel().open(ctx);
  }
  editStep(ctx: ActionContext) {
    this.editPanel().open(ctx);
  }

  deleteStep(ctx: ActionContext) {
    if (!ctx.parent) {
      this.campaign.strategy = undefined;
      this.forceRefresh();
      return;
    }

    if (ctx.parent.name == StrategyName.Filter) {
      (ctx.parent as Filter).nextStrategy = undefined;
      this.forceRefresh();
    }
    if (ctx.parent.name == StrategyName.Delay) {
      (ctx.parent as Delay).nextStrategy = undefined;
      this.forceRefresh();
    }
    if (ctx.parent.name == StrategyName.Parallel) {
      const ix = (ctx.parent as Parallel).children.indexOf(ctx.edited);
      (ctx.parent as Parallel).children.splice(ix, 1);
    }
    if (ctx.parent.name == StrategyName.Sequential) {
      const ix = (ctx.parent as Sequential).children.indexOf(ctx.edited);
      (ctx.parent as Sequential).children.splice(ix, 1);
    }
  }

  private async cloneCampaign() {
    const { campaign, locationFilters } = await this.campaignCloneService.cloneCampaign(this.originalCampaignId, this.advertiserId);

    this.campaign = campaign;
    this.isAdding = true;
    this.campaignId = undefined;
    // Store location filters to be set in setTimeout when viewChild is ready
    this.clonedLocationFilters = locationFilters;
  }

  forceRefresh() {

    const c = this.campaign;
    this.campaign = null;
    this.isLoading = true;
    setTimeout(() => {
      this.campaign = c;
      this.isLoading = false;
    }, 500);
  }

  // Called when user saves in panel-edit
  async onStrategySaved(ctx: ActionContext) {

    switch (ctx.operation) {
      case ActionOperation.Add:
        const isRootAdd = !this.campaign.strategy;
        if (isRootAdd) {
          this.campaign.strategy = ctx.edited;
          return;
        }

        if (ctx.original.name == StrategyName.Filter) {
          (ctx.original as Filter).nextStrategy = ctx.edited;
          return;
        }
        else if (ctx.original.name == StrategyName.Parallel) {
          const parallel = ctx.original as Parallel;
          if (!parallel.children) parallel.children = [];
          parallel.children.push(ctx.edited);
          return;
        }
        else if (ctx.original.name == StrategyName.Sequential) {
          const sequential = ctx.original as Sequential;
          if (!sequential.children) sequential.children = [];
          sequential.children.push(ctx.edited);
          return;
        }

        break;

      case ActionOperation.Edit:

        this.refAssignObj(ctx.original, ctx.edited);

        break;
    }

  }

  async onSave() {

    const model = this.campaignBase().getFormValue();
    if (!model)
      return;

    if (!this.campaign.strategy) {
      this.snackBar.open('At least one step is required', 'Ok', { duration: 3000 });
      return;
    }

    model.campaignId = this.campaignId;
    model.advertiserId = this.advertiserId;
    model.strategy = this.campaign.strategy;

    model.campaignLocationFilter = this.campaignLocationFilters().getFormValue();

    this.isLoading = true;
    try {
      await lastValueFrom(this.campaignsApi.createCampaign(model));

      this.snackBar.open(`Campaign ${model.title} was saved`, null, { duration: 3000 });
      this.router.navigate(['advertisers', this.advertiserId]);
    } catch (err: any) {
      if (err.status === 400 && err.response)
        new MessageDialogModel(err.response).show(this.dialog);
    }
    this.isLoading = false;
  }

  refAssignObj(ref: any, obj: any) {
    if (!ref) return;
    for (let key of Object.keys(ref))
      delete ref[key];

    if (!obj) return;
    for (let key of Object.keys(obj))
      ref[key] = obj[key];
  }


  mockCampaign() {
    const reward100 = new Reward({
      name: StrategyName.Reward, title: '100 Cheercoins are waiting for you!',
      coinReward: new CoinReward({ coins: 100 })
    });

    const reward1000 = new Reward({
      name: StrategyName.Reward, title: 'The final reward. 1000 cheercoins',
      coinReward: new CoinReward({ coins: 1000 })
    });

    const counter = new Counter({
      name: StrategyName.Counter, title: 'Collect 10 stamps to get to the next step.',
      stampCount: 0,
      maxStampCount: 10
    });

    const parallel = new Parallel({
      name: StrategyName.Parallel, title: 'Para1',
      children: [
        reward100,
        new Sequential({
          name: StrategyName.Sequential, title: 'Seq1',
          children: [counter, reward1000]
        })
      ]
    });

    const filter = new Filter({
      name: StrategyName.Filter, title: 'Buy 3 coffes',
      nextStrategy: parallel
    });

    return new Campaign({
      strategy: new Delay({
        name: StrategyName.Delay, title: 'Delay 1 day',
        duration: '00:00:00',
        nextStrategy: filter
      })
    });
  }

}
